import { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle as DlgTitle } from "@/components/ui/dialog";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Lead, LoginRecord, TimelineItem, LOAN_TYPE_LABELS, LoanType, getLegacyCustomerName } from "@/types";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useAppConfig } from "@/hooks/useAppConfig";
import { useBankers } from "@/hooks/useBankers";
import { addLeadStageChange } from "@/services/stageHistoryService";
import { ArrowLeft, Edit, Share2, LogIn, FileText, Star } from "lucide-react";
import { format } from "date-fns";
import { useModal } from "@/contexts/ModalContext";

// Safely convert any date value (JS Date, Firestore Timestamp, string, etc.) to a formatted string
function safeFormatDate(val: unknown, fmt: string = 'dd MMM yyyy'): string {
  if (!val) return '-';
  const d: Date =
    typeof (val as { toDate?: unknown }).toDate === 'function'
      ? (val as { toDate: () => Date }).toDate()
      : new Date(val as string | number | Date);
  if (isNaN(d.getTime())) return '-';
  return format(d, fmt);
}

interface LoginDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lead: Lead;
  onSave: (updatedLead: Partial<Lead>) => void;
}

function formatCurrency(amount: number) {
  if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(0)}L`;
  return `₹${amount.toLocaleString("en-IN")}`;
}

// Format number with Indian comma system (e.g., 50,00,000)
function formatIndianNumber(value: string | number): string {
  if (!value) return '';
  const numStr = value.toString().replace(/,/g, '');
  if (isNaN(Number(numStr))) return value.toString();
  const num = parseFloat(numStr);
  return num.toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

function parseIndianNumber(value: string): string {
  return value.replace(/,/g, '');
}

type ViewMode = 'list' | 'form';

export function LoginDialog({ open, onOpenChange, lead, onSave }: LoginDialogProps) {
  const { user } = useAuth();
  const { config } = useAppConfig();
  const { bankers, isFavorite: isBankerFavorite } = useBankers();
  const { registerModal, unregisterModal } = useModal();
  useEffect(() => {
    if (!open) return;
    registerModal('lead-login-dialog', () => onOpenChange(false), 1);
    return () => unregisterModal('lead-login-dialog');
  }, [open, onOpenChange, registerModal, unregisterModal]);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [editingLogin, setEditingLogin] = useState<LoginRecord | null>(null);

  // Share popup state
  const [sharePopupOpen, setSharePopupOpen] = useState(false);
  const [shareMessage, setShareMessage] = useState("");
  const [shareData, setShareData] = useState<{
    customerName: string;
    loanAmount: string;
    loanType: string;
    applicationNo: string;
    bankName: string;
    bankerName: string;
  } | null>(null);
  // Pending share data — set after submit so popup opens only after form collapses to list
  const [pendingShareData, setPendingShareData] = useState<{
    customerName: string;
    loanAmount: string;
    loanType: string;
    applicationNo: string;
    bankName: string;
    bankerName: string;
  } | null>(null);

  const [formData, setFormData] = useState({
    customerName: "",
    loanType: "" as string,
    applicationNumber: "",
    loanAmount: "",
    bankName: "",
    bankerId: "",
  });

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setViewMode('list');
      setEditingLogin(null);
      setSharePopupOpen(false);
      setShareData(null);
      setPendingShareData(null);
      setFormData({
        customerName: "",
        loanType: "",
        applicationNumber: "",
        loanAmount: "",
        bankName: "",
        bankerId: "",
      });
    }
  }, [open]);

  // Open share popup only after form has collapsed back to list view
  useEffect(() => {
    if (viewMode === 'list' && pendingShareData) {
      setShareData(pendingShareData);
      setShareMessage(buildShareMessage(pendingShareData));
      setSharePopupOpen(true);
      setPendingShareData(null);
    }
  }, [viewMode, pendingShareData]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-fill form when adding new or editing
  useEffect(() => {
    if (editingLogin) {
      setFormData({
        customerName: editingLogin.customerName || "",
        loanType: editingLogin.loanType || "",
        applicationNumber: editingLogin.applicationNumber || "",
        loanAmount: editingLogin.loanAmount?.toString() || "",
        bankName: editingLogin.bankName || "",
        bankerId: editingLogin.bankerId || "",
      });
      setViewMode('form');
    }
  }, [editingLogin]);

  const handleAddNew = () => {
    setEditingLogin(null);
    // Pre-fill from lead data
    const customerName = getLegacyCustomerName(lead);
    setFormData({
      customerName: customerName,
      loanType: lead.loanType || "",
      applicationNumber: "",
      loanAmount: lead.loanAmount?.toString() || "",
      bankName: lead.lender || lead.assignedBank || "",
      bankerId: lead.smContactId || "",
    });
    setViewMode('form');
  };

  const handleBack = () => {
    setViewMode('list');
    setEditingLogin(null);
  };

  const handleEditLogin = (record: LoginRecord) => {
    setEditingLogin(record);
  };

  const isFormValid = () => {
    return formData.customerName && formData.loanType && formData.applicationNumber
      && formData.loanAmount && formData.bankName && formData.bankerId;
  };

  // Determine user label for share message
  const getUserLabel = () => {
    if (user?.primaryRole === 'sales_agent' && user?.name) {
      return user.name;
    }
    return 'Direct';
  };

  const buildShareMessage = (data: {
    customerName: string;
    loanAmount: string;
    loanType: string;
    applicationNo: string;
    bankName: string;
    bankerName: string;
  }) => {
    const label = getUserLabel();
    const msg = `${label}: Login Done\nCustomer Name: ${data.customerName}\nLoan Amount: ${data.loanAmount}\nProduct: ${data.loanType}\nApplication No: ${data.applicationNo}\nBank Name: ${data.bankName}\nBanker Name: ${data.bankerName}`;
    return msg;
  };

  const handleWhatsAppShare = (message: string) => {
    const url = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const openShareForLogin = (record: LoginRecord) => {
    const loanTypeLabel = record.loanType && LOAN_TYPE_LABELS[record.loanType as LoanType]
      ? LOAN_TYPE_LABELS[record.loanType as LoanType]
      : record.loanType || '-';
    const sd = {
      customerName: record.customerName || '-',
      loanAmount: record.loanAmount ? formatCurrency(record.loanAmount) : '-',
      loanType: loanTypeLabel,
      applicationNo: record.applicationNumber || '-',
      bankName: record.bankName || '-',
      bankerName: record.bankerName || '-',
    };
    setShareData(sd);
    setShareMessage(buildShareMessage(sd));
    setSharePopupOpen(true);
  };

  // ─── Promote a login record to be the main login ───────────────────────────
  const handleSetAsMain = (recordId: string) => {
    const targetRecord = (lead.loginRecords || []).find(r => r.id === recordId);
    if (!targetRecord) return;

    // Mark the selected record as main, unmark all others
    const updatedRecords: LoginRecord[] = (lead.loginRecords || []).map(r => ({
      ...r,
      isMain: r.id === recordId,
    }));

    // Override lead-level fields from the new main record
    const updatedLead: Partial<Lead> = { loginRecords: updatedRecords };
    applyMainLoginToLead(targetRecord, updatedLead);

    onSave(updatedLead);
    toast.success('Main login updated — lead details synced to this login');
  };

  // Apply a login record's fields onto a lead partial (shared by submit + set-as-main)
  const applyMainLoginToLead = (record: LoginRecord, updatedLead: Partial<Lead>) => {
    if (record.customerName) {
      const parts = record.customerName.trim().split(/\s+/);
      updatedLead.firstName = parts[0] || lead.firstName;
      updatedLead.lastName = parts.slice(1).join(' ') || lead.lastName;
    }
    if (record.loanType) updatedLead.loanType = record.loanType as LoanType;
    if (record.loanAmount) updatedLead.loanAmount = record.loanAmount;
    if (record.bankName) {
      updatedLead.lender = record.bankName;
      updatedLead.assignedBank = record.bankName;
    }
    if (record.bankerId) updatedLead.smContactId = record.bankerId;
  };

  const handleSubmit = () => {
    if (!isFormValid()) {
      toast.error("Please fill all required fields (Customer Name, Loan Type, Application Number, Loan Amount, Bank Name, Banker Name)");
      return;
    }

    const loanAmount = Number(parseIndianNumber(formData.loanAmount));
    if (loanAmount <= 0) {
      toast.error("Loan Amount must be greater than 0");
      return;
    }

    // Resolve banker name
    const selectedBanker = formData.bankerId ? bankers.find(b => b.id === formData.bankerId) : null;

    // Determine isMain:
    // - If adding the first-ever login → auto main
    // - If editing a record that is already main → keep it main
    // - Otherwise (additional new login, or editing a non-main record) → not main
    const existingRecords = lead.loginRecords || [];
    const hasExistingMain = existingRecords.some(r => r.isMain);
    const isEditingMainRecord = editingLogin?.isMain === true;
    const isNewRecord = !editingLogin;
    const newIsMain = isEditingMainRecord || (isNewRecord && !hasExistingMain);

    const loginRecord: LoginRecord = {
      id: editingLogin?.id || `login_${Date.now()}`,
      customerName: formData.customerName,
      loanType: formData.loanType,
      applicationNumber: formData.applicationNumber,
      loanAmount: loanAmount,
      bankName: formData.bankName,
      bankerName: selectedBanker ? selectedBanker.name : '-',
      bankerId: formData.bankerId || undefined,
      isMain: newIsMain,
      createdAt: editingLogin?.createdAt || new Date(),
      createdBy: editingLogin?.createdBy || user?.name || 'Unknown',
      updatedAt: editingLogin ? new Date() : undefined,
      updatedBy: editingLogin ? user?.name : undefined,
    };

    // Update login records array; if this record becomes main, unmark any other main
    let updatedLoginRecords: LoginRecord[];
    if (editingLogin) {
      updatedLoginRecords = existingRecords.map(r => {
        if (r.id === editingLogin.id) return loginRecord;
        // If this edit promotes to main, demote others
        if (newIsMain) return { ...r, isMain: false };
        return r;
      });
    } else {
      // New record: if it's main, demote any existing mains (shouldn't happen, but defensive)
      updatedLoginRecords = newIsMain
        ? [...existingRecords.map(r => ({ ...r, isMain: false })), loginRecord]
        : [...existingRecords, loginRecord];
    }

    // Create timeline entry
    const timelineEntry: TimelineItem = {
      id: `timeline_${Date.now()}`,
      type: 'note',
      content: editingLogin
        ? `Updated login record - Application #${formData.applicationNumber}${newIsMain ? ' (Main)' : ''}`
        : `Login filed - Application #${formData.applicationNumber} at ${formData.bankName}${newIsMain ? ' (Main)' : ''}`,
      createdAt: new Date(),
      createdBy: user?.name || 'Unknown',
    };

    const loanTypeLabel = formData.loanType && LOAN_TYPE_LABELS[formData.loanType as LoanType]
      ? LOAN_TYPE_LABELS[formData.loanType as LoanType]
      : formData.loanType;

    const updatedLead: Partial<Lead> = {
      loginRecords: updatedLoginRecords,
      timeline: [timelineEntry, ...(lead.timeline || [])],
    };

    // Only override lead-level fields from the MAIN login record
    if (newIsMain) {
      applyMainLoginToLead(loginRecord, updatedLead);
    }

    // Auto-move to logged_in stage if currently at an earlier stage (new_lead, in_progress, ip_sanction)
    const preLoginStages: string[] = ['new_lead', 'in_progress', 'ip_sanction'];
    if (!editingLogin && preLoginStages.includes(lead.status)) {
      updatedLead.status = 'logged_in';
      addLeadStageChange({
        leadId: lead.id,
        fromStatus: lead.status,
        toStatus: 'logged_in',
        changedBy: user?.userId || user?.id || 'Unknown',
        changedAt: new Date(),
        officeId: user?.officeId || '',
      });
    }

    onSave(updatedLead);

    toast.success(
      editingLogin
        ? "Login record updated successfully"
        : "Login filed successfully",
      {
        description: updatedLead.status === 'logged_in' && updatedLead.status !== lead.status
          ? 'Lead auto-moved to Logged In stage'
          : (newIsMain && existingRecords.length > 0 && !isEditingMainRecord
            ? 'Set as main login — lead details updated'
            : undefined)
      }
    );

    // Show share popup after new login (not for edits)
    if (!editingLogin) {
      const sd = {
        customerName: formData.customerName,
        loanAmount: formatCurrency(loanAmount),
        loanType: loanTypeLabel,
        applicationNo: formData.applicationNumber || '-',
        bankName: formData.bankName || '-',
        bankerName: selectedBanker ? selectedBanker.name : '-',
      };
      setPendingShareData(sd);
      setViewMode('list');
      setEditingLogin(null);
    } else {
      setViewMode('list');
      setEditingLogin(null);
    }
  };

  // Share popup dialog
  const renderSharePopup = () => {
    if (!shareData) return null;
    return (
      <Dialog open={sharePopupOpen} onOpenChange={(open) => {
        setSharePopupOpen(open);
        if (!open) {
          setShareData(null);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DlgTitle className="text-lg font-bold">Login Summary</DlgTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea
              value={shareMessage}
              onChange={(e) => setShareMessage(e.target.value)}
              rows={9}
              className="text-sm font-mono leading-relaxed resize-none"
            />
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setSharePopupOpen(false);
                  setShareData(null);
                  // Edit the main login (the one that drives lead fields)
                  const mainRecord = lead.loginRecords?.find(r => r.isMain)
                    ?? lead.loginRecords?.[lead.loginRecords.length - 1];
                  if (mainRecord) handleEditLogin(mainRecord);
                }}
              >
                <Edit className="h-4 w-4 mr-1" />
                Edit
              </Button>
              <Button
                className="flex-1 bg-green-600 hover:bg-green-700"
                onClick={() => handleWhatsAppShare(shareMessage)}
              >
                <Share2 className="h-4 w-4 mr-2" />
                WhatsApp Share
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  // ─── LIST VIEW ────────────────────────────────────────────────────
  if (viewMode === 'list') {
    return (
      <>
        <Sheet open={open} onOpenChange={onOpenChange}>
          <SheetContent side="bottom" className="h-[90vh] rounded-t-3xl overflow-y-auto">
            <SheetHeader className="pb-4">
              <SheetTitle className="text-xl font-bold">Login Management</SheetTitle>
              <p className="text-sm text-muted-foreground">
                Track loan login filings for this customer
              </p>
            </SheetHeader>

            <div className="space-y-5 pb-8">
              {/* Login History */}
              {lead.loginRecords && lead.loginRecords.length > 0 && (
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Login History
                  </h3>
                  <p className="text-xs text-muted-foreground -mt-1">
                    The <span className="font-semibold text-yellow-600">★ Main</span> login drives lead details and leaderboard stats.
                  </p>
                  {lead.loginRecords.map((record, index) => (
                    <Card key={record.id} className={`border-l-4 ${record.isMain ? 'border-l-yellow-400' : 'border-l-blue-500'}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <Badge variant="secondary">
                                Login #{index + 1}
                              </Badge>
                              {record.isMain && (
                                <Badge className="bg-yellow-100 text-yellow-800 border border-yellow-300 hover:bg-yellow-100">
                                  <Star className="h-3 w-3 mr-1 fill-yellow-500 text-yellow-500" />
                                  Main
                                </Badge>
                              )}
                            </div>
                            <p className="font-bold text-lg text-blue-700">
                              {record.customerName}
                            </p>
                            <p className="text-sm text-muted-foreground mt-1">
                              {record.loanType && LOAN_TYPE_LABELS[record.loanType as LoanType]
                                ? LOAN_TYPE_LABELS[record.loanType as LoanType]
                                : record.loanType} • {formatCurrency(record.loanAmount)}
                            </p>
                            {record.applicationNumber && (
                              <p className="text-xs text-muted-foreground">
                                App #: {record.applicationNumber}
                              </p>
                            )}
                            {record.bankName && (
                              <p className="text-xs text-muted-foreground">
                                Bank: {record.bankName}
                              </p>
                            )}
                            {record.bankerName && (
                              <p className="text-xs text-muted-foreground">
                                Banker: {record.bankerName}
                              </p>
                            )}
                            <p className="text-xs text-muted-foreground mt-1">
                              Filed on {safeFormatDate(record.createdAt)} by {record.createdBy}
                            </p>
                            {record.updatedAt && (
                              <p className="text-xs text-muted-foreground">
                                Updated by {record.updatedBy} on {safeFormatDate(record.updatedAt)}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-col gap-1 shrink-0">
                            {/* Set as Main — only show on non-main cards */}
                            {!record.isMain && (
                              <Button
                                variant="ghost"
                                size="sm"
                                title="Set as Main Login"
                                onClick={() => handleSetAsMain(record.id)}
                                className="text-yellow-600 hover:text-yellow-700"
                              >
                                <Star className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openShareForLogin(record)}
                              className="text-green-600"
                            >
                              <Share2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditLogin(record)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* Add New Login — always shown so users can file to multiple banks */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100 text-blue-700">
                    <LogIn className="h-4 w-4" />
                  </div>
                  <h3 className="font-semibold text-sm">
                    {!lead.loginRecords?.length ? 'File First Login' : 'File Another Login'}
                  </h3>
                </div>

                <Card
                  className="cursor-pointer hover:shadow-md transition-all border hover:border-blue-400/50"
                  onClick={handleAddNew}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 text-blue-700 shrink-0">
                        <LogIn className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold">New Login Filing</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {!lead.loginRecords?.length
                            ? 'Fill login details and track application'
                            : 'File to an additional bank — use ★ to promote as main'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </SheetContent>
        </Sheet>
        {renderSharePopup()}
      </>
    );
  }

  // ─── FORM VIEW ────────────────────────────────────────────────────
  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="h-[90vh] rounded-t-3xl overflow-y-auto">
          <SheetHeader className="pb-4">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBack}
                className="shrink-0 -ml-2"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="flex-1">
                <SheetTitle className="text-xl font-bold">
                  {editingLogin ? 'Edit' : 'File'} Login
                </SheetTitle>
                <p className="text-sm text-muted-foreground">
                  {editingLogin ? 'Update login details' : 'Enter loan login information'}
                </p>
              </div>
            </div>
          </SheetHeader>

          <div className="space-y-5 pb-8">
            {/* Customer Full Name */}
            <div className="space-y-2">
              <Label htmlFor="customerName">Customer Full Name <span className="text-red-500">*</span></Label>
              <Input
                id="customerName"
                placeholder="Ex - Rajesh Kumar"
                value={formData.customerName}
                onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                className="h-12 placeholder:text-muted-foreground/50"
              />
            </div>

            {/* Loan Type */}
            <div className="space-y-2">
              <Label>Loan Type <span className="text-red-500">*</span></Label>
              <SearchableSelect
                options={Object.entries(LOAN_TYPE_LABELS).map(([value, label]) => ({
                  value,
                  label,
                }))}
                value={formData.loanType}
                onValueChange={(v) => setFormData({ ...formData, loanType: v })}
                placeholder="Select loan type"
                searchPlaceholder="Search loan types..."
                emptyText="No loan type found."
              />
            </div>

            {/* Loan Application Number */}
            <div className="space-y-2">
              <Label htmlFor="applicationNumber">Loan Application Number <span className="text-red-500">*</span></Label>
              <Input
                id="applicationNumber"
                placeholder="Ex - HL2024012345"
                value={formData.applicationNumber}
                onChange={(e) => setFormData({ ...formData, applicationNumber: e.target.value })}
                className="h-12 placeholder:text-muted-foreground/50"
              />
            </div>

            {/* Loan Amount */}
            <div className="space-y-2">
              <Label htmlFor="loanAmount">Loan Amount (₹) <span className="text-red-500">*</span></Label>
              <Input
                id="loanAmount"
                type="text"
                placeholder="Ex - 50,00,000"
                value={formData.loanAmount ? formatIndianNumber(formData.loanAmount) : ''}
                onChange={(e) => {
                  const rawValue = parseIndianNumber(e.target.value);
                  setFormData({ ...formData, loanAmount: rawValue });
                }}
                onWheel={(e) => e.currentTarget.blur()}
                className="h-12 placeholder:text-muted-foreground/50"
              />
            </div>

            {/* Bank Name */}
            <div className="space-y-2">
              <Label>Bank <span className="text-red-500">*</span></Label>
              <SearchableSelect
                options={config.banks.map(bank => ({ value: bank, label: bank }))}
                value={formData.bankName}
                onValueChange={(v) => setFormData({ ...formData, bankName: v })}
                placeholder="Select bank name"
                searchPlaceholder="Search banks..."
                emptyText="No bank found."
              />
            </div>

            {/* Banker Name */}
            <div className="space-y-2">
              <Label>Banker Name <span className="text-red-500">*</span></Label>
              <SearchableSelect
                options={[
                  ...[...bankers]
                    .sort((a, b) => {
                      const aFav = isBankerFavorite(a.id);
                      const bFav = isBankerFavorite(b.id);
                      if (aFav && !bFav) return -1;
                      if (!aFav && bFav) return 1;
                      return a.name.localeCompare(b.name);
                    })
                    .map(banker => ({
                      value: banker.id,
                      label: `${isBankerFavorite(banker.id) ? '★ ' : ''}${banker.name} - ${banker.bank}`,
                      displayLabel: banker.name
                    }))
                ]}
                value={formData.bankerId || ""}
                onValueChange={(v) => setFormData({ ...formData, bankerId: v })}
                placeholder="Select banker name"
                searchPlaceholder="Search bankers..."
                emptyText="No banker found."
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-4 sticky bottom-0 bg-background pb-4">
            <Button
              variant="outline"
              className="flex-1"
              onClick={handleBack}
            >
              Cancel
            </Button>
            <Button
              className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600"
              onClick={handleSubmit}
              disabled={!isFormValid()}
            >
              {editingLogin ? 'Update' : 'Submit'} Login
            </Button>
          </div>
        </SheetContent>
      </Sheet>
      {renderSharePopup()}
    </>
  );
}
